import { CancelRounded, EditRounded, ExpandMore, SaveRounded } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { SyntheticEvent, useCallback, useContext, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { setSnackbar } from "../../../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../../../util/state/globalVariables";
import { editBookingFields, useBookingByIdWithMetaQueryObserver } from "../../../../../../../api/services";
import { FormAutocomplete2, FormCheckbox, FormTextField } from "../../../../../../../components/fields";
import { BookingEditableField, VipType, RouteParams } from "../../../../../../../types";
import { addEditIconToEndAdornment, getDirtyValues } from "../../../../../../../utils";
import { IFormValues } from "../../../../../types";
import { useRecoilValue } from "recoil";
import { cigList } from "../../../../../recoil";
import BusinessAgreementHelperText from "./helper-text/BusinessAgreementHelperText";
import RateHelperText from "./helper-text/RateHelperText";

const RentalTypeNavigation = (props: { rentalCode: string, label: string }) => {

    const { t } = useTranslation('booking');
    return(
        <Stack direction="row" justifyContent="space-between" alignItems="start">
            {props?.rentalCode &&
                    <Link
                        component={RouterLink}
                        to={`/network/ra-detail/${props.rentalCode}`}
                        target="_blank"
                    >
                        {t(`${props.label}`)}
                    </Link>
            }
        </Stack>
    );
}

const OtherDetails = () => {

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { id } = useParams<RouteParams>();

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();
    const formState = useFormState<IFormValues>();
    const cig = useRecoilValue<string[]>(cigList);

    const rentalAgreement = useWatch<IFormValues, 'otherDetails.rentalAgreement'>({
        name: 'otherDetails.rentalAgreement'
    });

    const parentRa = useWatch<IFormValues, 'otherDetails.parentRa'>({
        name: 'otherDetails.parentRa'
    });

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);

    const editBookingFieldsMutation = useMutation(
        editBookingFields,
        {
            onSuccess: () => {
                bookingQuery.refetch();
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
                formContext.resetField('otherDetails');
            }
        }
    )

    const [editable, setEditable] = useState<boolean>(false);

    const onEditClick = useCallback((event: SyntheticEvent) => {
        event.stopPropagation();
        setEditable(true);
    }, []);
    const onCancelClick = useCallback((event: SyntheticEvent) => {
        event.stopPropagation();
        formContext.resetField('otherDetails');
        setEditable(false);
    }, []);
    const onSaveClick = useCallback((event: SyntheticEvent) => {
        event.stopPropagation();
        if (!id) {
            return;
        }
        const {vip, flightNumber, dossierNumber, replacementPlateNumber, cig, specialConditionsAccepted} = formContext.getValues("otherDetails");
        editBookingFieldsMutation.mutate({
            id,
            vip,
            flightNumber,
            dossierNumber,
            replacementPlateNumber,
            cig,
            specialConditionsAccepted
        });
        setEditable(false);
    }, [JSON.stringify(formState.dirtyFields), id]);

    return (
        <Accordion defaultExpanded>
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                    '.MuiAccordionSummary-content': {
                        justifyContent: "space-between",
                        alignItems: "center"
                    }
                }}
            >
                <Typography variant="h5">
                    {t('label.otherDetails')}
                </Typography>

                {bookingQuery.data && bookingQuery.data.editableFields.length > 0 && !bookingQuery.data.booking.subscriptionNumber && !bookingQuery.data.booking.subscriptionMonth && (
                    editable ?
                        <Stack direction="row" alignItems="center">
                            <IconButton color="accent" disabled={!formState.isDirty} onClick={onSaveClick}>
                                <SaveRounded fontSize="inherit" />
                            </IconButton>
                            <IconButton color="accent" onClick={onCancelClick}>
                                <CancelRounded fontSize="inherit" />
                            </IconButton>
                        </Stack> :
                        <IconButton color="accent" onClick={onEditClick}>
                            <EditRounded fontSize="inherit" />
                        </IconButton>
                )}
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={1} columnSpacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormAutocomplete2<IFormValues>
                            name="otherDetails.vip"
                            label={t('label.vip')}
                            options={Object.values(VipType)}
                            readOnly={!(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.Vip))}
                            getOptionLabel={option => t(`vip_type.${option}`)}
                            renderTextFieldProps={(params) => ({
                                ...params,
                                InputProps: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        editable &&
                                        bookingQuery.data?.editableFields.includes(BookingEditableField.Vip) &&
                                        addEditIconToEndAdornment(params.InputProps.endAdornment)
                                    )
                                },
                                InputLabelProps: {
                                    shrink: true
                                }
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.fastLine"
                            label={t('label.fastLine')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.parentRa"
                            label={t('label.parentRa')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                            helperText={<RentalTypeNavigation rentalCode={parentRa} label={'label.parentRaInfo'}/>}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.rentalAgreement"
                            label={t('label.rentalAgreement')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                            helperText={<RentalTypeNavigation rentalCode={rentalAgreement} label={'label.rentalAgreementInfo'}/>}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.segment"
                            label={t('label.segment')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.category"
                            label={t('label.category')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.product"
                            label={t('label.product')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.rate"
                            label={t('label.rate')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                            helperText={ <RateHelperText />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.leisureCode"
                            label={t('label.promoCouponLeisureCode')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.codeDescription"
                            label={t('label.codeDescription')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.greenDiscount"
                            label={t('label.greenDiscount')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.fcaBankCardNumber"
                            label={t('label.fcaBankCardNumber')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.discountOverprice"
                            label={t('label.discountOverprice')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.flightNumber"
                            label={t('label.flightNumber')}
                            InputProps={{
                                readOnly: !(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.FlightNumber)),
                                endAdornment: editable && bookingQuery.data?.editableFields.includes(BookingEditableField.FlightNumber) && (
                                    <InputAdornment position="end">
                                        <EditRounded />
                                    </InputAdornment>
                                )
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.customerCode"
                            label={t('label.businessAgreement')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                            helperText={ <BusinessAgreementHelperText /> }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.jimpiCustomerCode"
                            label={t('label.jimpiCustomerCode')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.rentwayNumber"
                            label={t('label.rentwayNumber')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.dossierNumber"
                            label={t('label.dossierNumber')}
                            InputProps={{
                                readOnly: !(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.DossierNumber)),
                                endAdornment: editable && bookingQuery.data?.editableFields.includes(BookingEditableField.DossierNumber) && (
                                    <InputAdornment position="end">
                                        <EditRounded />
                                    </InputAdornment>
                                )
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.assignedPlateNumber"
                            label={t('label.assignedPlateNumber')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.replacementPlateNumber"
                            label={t('label.replacementPlateNumber')}
                            InputProps={{
                                readOnly: !(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.ReplacementPlateNumber)),
                                endAdornment: editable && bookingQuery.data?.editableFields.includes(BookingEditableField.ReplacementPlateNumber) && (
                                    <InputAdornment position="end">
                                        <EditRounded />
                                    </InputAdornment>
                                )
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
{/*                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.cig"
                            label={t('label.cig')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>*/}
                        <Grid item xs={12} sm={6}>
                            <FormAutocomplete2<IFormValues>
                                name="otherDetails.cig"
                                label={t('label.cig')}
                                options={cig}
                                readOnly={!(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.Cig))}
                                getOptionLabel={option => option}
                                renderTextFieldProps={(params) => ({
                                    ...params,
                                    InputProps: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            editable &&
                                            bookingQuery.data?.editableFields.includes(BookingEditableField.Cig) &&
                                            addEditIconToEndAdornment(params.InputProps.endAdornment)
                                        )
                                    },
                                    InputLabelProps: {
                                        shrink: true
                                    }
                                })}
                            />
                        </Grid>

                    <Grid container item xs={12} sm={6} mt={2} justifyContent="space-between" alignItems="center">
                        <FormCheckbox<IFormValues>
                            name="otherDetails.specialConditionsAccepted"
                            label={t('label.specialConditionsAccepted')}
                            disabled={!(editable && bookingQuery.data?.editableFields.includes(BookingEditableField.SpecialConditionsAccepted))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.affiliationCode"
                            label={t('label.affiliationCode')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                        />
                    </Grid>
                    <Grid container item xs={12} sm={6} mt={2} justifyContent="space-between" alignItems="center">
                        <FormCheckbox<IFormValues>
                            name="otherDetails.customerAtDesk"
                            label={t('label.customerAtDesk')}
                            disabled={true}
                        />
                    </Grid>
                    <Grid container item xs={12} sm={6} mt={2} justifyContent="space-between" alignItems="center">
                        <FormCheckbox<IFormValues>
                            name="otherDetails.keyless"
                            label={t('label.keyless')}
                            disabled={true}
                        />
                    </Grid>
                    <Grid container item xs={12} sm={6} mt={2} justifyContent="space-between" alignItems="center">
                        <FormCheckbox<IFormValues>
                            name="otherDetails.rce"
                            label={t('label.rce')}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormTextField<IFormValues>
                            name="otherDetails.errorCode"
                            label={t('label.errorCode')}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                shrink: true
                            }}
                            disabled={true}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion >
    )
};

export default OtherDetails;
